import CrudTable from "@/components/CrudTable";
import { inr } from "@/lib/format";
import { CalendarClock } from "lucide-react";

const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d.slice(0, 10)) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
};

export default function Taxation() {
  const columns = [
    { key: "tax_type", label: "Tax Type" },
    { key: "period", label: "Period" },
    { key: "due_date", label: "Due Date" },
    { key: "amount", label: "Amount", type: "currency" },
    { key: "payment_status", label: "Status", type: "badge" },
    { key: "filed_date", label: "Filed/Paid" },
    { key: "penalty_incurred", label: "Penalty?" },
    { key: "penalty_amount", label: "Penalty ₹", type: "currency" },
  ];
  const fields = [
    { key: "tax_type", label: "Tax Type", type: "select", required: true, options: ["GST", "TDS", "Income Tax", "PF", "ESI", "Professional Tax", "Advance Tax", "Other"] },
    { key: "period", label: "Period / Month", type: "text", required: true },
    { key: "due_date", label: "Due Date", type: "date", required: true },
    { key: "amount", label: "Amount (₹)", type: "number", required: true },
    { key: "payment_status", label: "Payment Status", type: "select", options: ["Pending", "Paid", "Overdue"], required: true },
    { key: "filed_date", label: "Filed / Paid Date", type: "date" },
    { key: "penalty_incurred", label: "Penalty Incurred", type: "select", options: ["N", "Y"] },
    { key: "penalty_amount", label: "Penalty Amount (₹)", type: "number" },
    { key: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  const renderTop = (rows) => {
    const upcoming = rows
      .filter((r) => r.payment_status !== "Paid" && r.due_date)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
    return (
      <div>
        <div className="flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4 text-[#C9A24B]" /><h3 className="font-heading font-medium">Compliance Calendar — upcoming due dates</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="compliance-calendar">
          {upcoming.length === 0 && <p className="text-sm text-slate-500">No pending filings — fully compliant.</p>}
          {upcoming.map((r) => {
            const d = daysUntil(r.due_date);
            const overdue = d < 0 || r.payment_status === "Overdue";
            const soon = d >= 0 && d <= 7;
            const color = overdue ? "border-rose-600/40 bg-rose-600/10" : soon ? "border-amber-500/40 bg-amber-500/10" : "border-[#334155] bg-[#1E293B]";
            return (
              <div key={r.id} className={`rounded-xl border p-4 ${color}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold text-sm">{r.tax_type}</span>
                  <span className={`text-xs font-medium ${overdue ? "text-rose-400" : soon ? "text-amber-400" : "text-slate-400"}`}>
                    {overdue ? `${Math.abs(d)}d overdue` : `${d}d left`}
                  </span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{r.period} · due {r.due_date}</div>
                <div className="text-lg font-heading font-bold tabular-nums mt-2">{inr(r.amount)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <CrudTable
      testid="tax" endpoint="taxes"
      title="Taxation & Compliance" subtitle="Every upcoming due date in one place with 7/3/1-day reminders. Overdue items escalate to the Dashboard and notification bell — built so nothing is ever missed."
      addLabel="Add Tax Entry" columns={columns} fields={fields}
      defaults={{ payment_status: "Pending", penalty_incurred: "N" }} renderTop={renderTop}
    />
  );
}
